import React, { useState, useEffect } from 'react';
import { Card, Table, Button, Alert, Form } from 'react-bootstrap';
import { getUsers, getUserRoles, assignRoleToUser, removeRoleFromUser } from '../services/api/userApi';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faUserPlus, faUserMinus } from '@fortawesome/free-solid-svg-icons';

const RoleUsers = ({ role, onBack }) => {
  const [users, setUsers] = useState([]);
  const [roleUsers, setRoleUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [selectedUser, setSelectedUser] = useState('');

  useEffect(() => {
    fetchData();
  }, [role]);

  const fetchData = async () => {
    try {
      setLoading(true);
      // Get all users
      const usersResponse = await getUsers();
      setUsers(usersResponse.data);
      
      // For each user, check if they have this role
      const usersWithRoleStatus = await Promise.all(
        usersResponse.data.map(async (user) => {
          const userRolesResponse = await getUserRoles(user.id);
          const hasRole = userRolesResponse.data.some(r => r.id === role.id);
          return { ...user, hasRole };
        })
      );
      
      // Filter users who have the role
      const usersInRole = usersWithRoleStatus.filter(user => user.hasRole);
      setRoleUsers(usersInRole);
    } catch (error) {
      setError('Failed to fetch users data');
    } finally {
      setLoading(false);
    }
  };

  const handleAddUser = async () => {
    if (!selectedUser) return;
    
    try {
      await assignRoleToUser(selectedUser, role.id);
      fetchData();
      setSelectedUser('');
    } catch (error) {
      setError('Failed to assign user to role');
    }
  };

  const handleRemoveUser = async (userId) => {
    try {
      await removeRoleFromUser(userId, role.id);
      fetchData();
    } catch (error) {
      setError('Failed to remove user from role');
    }
  };

  const getNonRoleUsers = () => {
    const roleUserIds = new Set(roleUsers.map(u => u.id));
    return users.filter(u => !roleUserIds.has(u.id));
  };

  if (loading) return <div>Loading users...</div>;

  return (
    <div>
      <div className="d-flex justify-content-between align-items-center mb-3">
        <h2>Manage Users for {role.name}</h2>
        <Button variant="secondary" onClick={onBack}>
          Back to Roles
        </Button>
      </div>

      {error && <Alert variant="danger">{error}</Alert>}

      <Card className="mb-4">
        <Card.Header>Add User to Role</Card.Header>
        <Card.Body>
          <div className="d-flex">
            <Form.Select 
              value={selectedUser} 
              onChange={(e) => setSelectedUser(e.target.value)}
              className="me-2"
            >
              <option value="">Select a user</option>
              {getNonRoleUsers().map(user => (
                <option key={user.id} value={user.id}>
                  {user.firstName} {user.lastName} ({user.username})
                </option>
              ))}
            </Form.Select>
            <Button 
              variant="primary" 
              onClick={handleAddUser}
              disabled={!selectedUser}
            >
              <FontAwesomeIcon icon={faUserPlus} className="me-1" /> Add User
            </Button>
          </div>
        </Card.Body>
      </Card>

      <Card>
        <Card.Header>Users in Role</Card.Header>
        <Card.Body>
          <Table striped bordered hover>
            <thead>
              <tr>
                <th>Username</th>
                <th>Name</th>
                <th>Email</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {roleUsers.length === 0 ? (
                <tr>
                  <td colSpan="4" className="text-center">No users assigned to this role</td>
                </tr>
              ) : (
                roleUsers.map(user => (
                  <tr key={user.id}>
                    <td>{user.username}</td>
                    <td>{user.firstName} {user.lastName}</td>
                    <td>{user.email}</td>
                    <td>
                      <Button
                        variant="outline-danger"
                        size="sm"
                        onClick={() => handleRemoveUser(user.id)}
                        title="Remove User from Role"
                      >
                        <FontAwesomeIcon icon={faUserMinus} /> <span className="visually-hidden">Remove</span>
                      </Button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </Table>
        </Card.Body>
      </Card>
    </div>
  );
};

export default RoleUsers;